import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import Typography from '@material-ui/core/Typography';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import DateTImeDiv from '../../atoms/Div/DateTImeDiv';
import AlarmKindDiv from '../Div/AlarmKindDiv';
import AlarmStateDiv from '../Div/AlarmStateDiv';
import { AppContext } from '../../../App';
import SimpleDialog from '../../atoms/Dialog/SimpleDialog';

const useStyles = makeStyles(
  createStyles({
    tableContainer: {
      width: '90%',
      maxHeight: 'calc(100vh - 240px)',
    },
    //テーブル外枠
    tableStyle: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
    },
    //テーブルヘッダセル
    tableHeaderCell: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      backgroundColor: 'grey',
      textAlign: 'center',
      color: 'white',
    },
    //テーブルボディーセル
    tableCell: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      textAlign: 'center',
      // minWidth: '100px',
      padding: '1px',
    },
    tableRow: {
      height: '10',
    },
  })
);

export type ALM_KIND_SET = {
  TYP_NO: Number;
  TYP_NAME: string;
};

export type ALM_SMM_DAT = {
  COL_DATE: Date;
  PLC_NO: Number;
  AI_CARD: Number;
  AI_CH: Number;
  JUDGE_NO: Number;
  ALM_KIND: Number;
  ALM_TYP_NO: Number;
  MESSAGE: string;
  STAT: Number;
  DATA: Number;
  LIMIT: Number;
  CHECKED: Number;
  DEV_NO: string;
  DEV_NAME: string;
  ITM_NAME: string;
  JUDGE_MODE: Number;
  SET_NO: Number;
  ALM_CODE: string;
};

type AlarmDatas = Array<{
  ALM_SMM_DAT: ALM_SMM_DAT;
  ALM_KIND_SET: ALM_KIND_SET;
}>;

const RELOAD_SPAN: number = 10000;

const AlarmGrid: React.FC = () => {
  const classes = useStyles();
  const { login } = useContext(AppContext);
  const [alarmArray, setAlarmArray] = useState<AlarmDatas>([]);
  const [messageDialogOpen, setMessageDialogOpen] = React.useState(false);
  const [showMessage, setShowMessage] = React.useState<string>('');

  const { t } = useTranslation();

  const getData = () => {
    fetch('api/AlarmData')
      .then((res) => {
        if (!res.ok) {
          setShowMessage(
            t(
              'AlarmGrid.ログイン期限が切れました_もう一度ログインしてください',
              'ログイン期限が切れました_もう一度ログインしてください'
            )
          );
          setMessageDialogOpen(true);
          throw new Error('');
        }
        return res.json();
      })
      .then((result: AlarmDatas) => {
        setAlarmArray(result);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getColor = (sate: Number): string => {
    switch (sate) {
      case 1:
        return 'red';
      case 2:
        return '#ee7800';
      case 3:
        return 'red';
      case 4:
        return 'red';
      default:
        return 'black';
    }
  };

  useEffect(() => {
    getData();
    const interval = setInterval(() => {
      getData();
    }, RELOAD_SPAN);
    return () => {
      clearInterval(interval);
    };
  }, []);

  /**
   * 状態が復帰の場合メッセージを空白にする
   * @param data
   * @returns
   */
  const getAlarmMessage = (data: ALM_SMM_DAT): string => {
    if (data.STAT === 2) {
      return '';
    }
    return data.MESSAGE;
  };

  const getAlarmInfo = (): any[] => {
    const items: any[] = [];
    for (let i = 0; i < 3; i++) {
      if (alarmArray.length >= i + 1) {
        var row = alarmArray[i];
        items.push(
          <TableRow key={i + 1} className={classes.tableRow}>
            <TableCell
              className={classes.tableCell}
              style={{ minWidth: '40px', maxWidth: '50px', color: getColor(row.ALM_SMM_DAT.ALM_KIND) }}
            >
              <DateTImeDiv value={row.ALM_SMM_DAT.COL_DATE} format={'YY/MM/DD HH:mm:ss'} />
            </TableCell>
            <TableCell
              className={classes.tableCell}
              style={{ minWidth: '40px', maxWidth: '50px', color: getColor(row.ALM_SMM_DAT.ALM_KIND) }}
            >
              <AlarmKindDiv kind={row.ALM_SMM_DAT.ALM_KIND} />
            </TableCell>
            <TableCell
              className={classes.tableCell}
              style={{ minWidth: '50px', maxWidth: '60px', color: getColor(row.ALM_SMM_DAT.ALM_KIND) }}
            >
              {row.ALM_SMM_DAT.DEV_NAME}
            </TableCell>
            <TableCell
              className={classes.tableCell}
              style={{ minWidth: '30px', maxWidth: '50px', color: getColor(row.ALM_SMM_DAT.ALM_KIND) }}
            >
              {row.ALM_SMM_DAT.ITM_NAME}
            </TableCell>
            <TableCell
              className={classes.tableCell}
              style={{ minWidth: '50px', maxWidth: '60px', color: getColor(row.ALM_SMM_DAT.ALM_KIND) }}
            >
              {row.ALM_KIND_SET?.TYP_NAME ?? ''}
            </TableCell>
            <TableCell
              className={classes.tableCell}
              style={{ minWidth: '160px', maxWidth: '200px', color: getColor(row.ALM_SMM_DAT.ALM_KIND) }}
            >
              {getAlarmMessage(row.ALM_SMM_DAT)}
            </TableCell>
            <TableCell
              className={classes.tableCell}
              style={{ minWidth: '30px', maxWidth: '50px', color: getColor(row.ALM_SMM_DAT.ALM_KIND) }}
            >
              <AlarmStateDiv state={row.ALM_SMM_DAT.STAT} devName={row.ALM_SMM_DAT.DEV_NAME} />
            </TableCell>
          </TableRow>
        );
      } else {
        items.push(
          <TableRow key={i + 1} className={classes.tableRow}>
            <TableCell className={classes.tableCell} style={{ color: getColor(0) }}>
              &nbsp;
            </TableCell>
            <TableCell className={classes.tableCell} style={{ color: getColor(0) }}>
              &nbsp;
            </TableCell>
            <TableCell className={classes.tableCell} style={{ color: getColor(0) }}>
              &nbsp;
            </TableCell>
            <TableCell className={classes.tableCell} style={{ color: getColor(0) }}>
              &nbsp;
            </TableCell>
            <TableCell className={classes.tableCell} style={{ color: getColor(0) }}>
              &nbsp;
            </TableCell>
            <TableCell className={classes.tableCell} style={{ color: getColor(0) }}>
              &nbsp;
            </TableCell>
            <TableCell className={classes.tableCell} style={{ color: getColor(0) }}>
              &nbsp;
            </TableCell>
          </TableRow>
        );
      }
    }
    return items;
  };

  return (
    <>
      <TableContainer>
        <Table className={classes.tableStyle} aria-label="a dense table">
          <TableBody>{getAlarmInfo()}</TableBody>
        </Table>
      </TableContainer>
      <SimpleDialog
        open={messageDialogOpen}
        onClose={() => {
          login.logout();
          setMessageDialogOpen(false);
        }}
        title={t('AlarmGrid.エラー画面', 'エラー画面')}
        okText={t('AlarmGrid.OK', 'OK')}
        maxWidth={'sm'}
      >
        <Typography>{showMessage}</Typography>
      </SimpleDialog>
    </>
  );
};
export default AlarmGrid;
